import { EnvironmentOutlined, InboxOutlined, SearchOutlined, MessageOutlined, CheckOutlined, TeamOutlined } from "@ant-design/icons";
import { Avatar, Badge, Button, Card, Col, Row, Tabs, Modal, Input, Spin, Tooltip } from "antd";
import ArchiveIcon from "../../Assets/archives.svg";
import UnarchiveIcon from "../../Assets/unarchive.svg";
import LeftArrow from "../../Assets/leftArrow.svg";
import MyIcon from "../../Assets/share.svg";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API_MANAGER from "../../API";
import { message } from "antd";
// import { getSocket, initSocket } from "../../components/Socket/socket";
import { ensureSocketConnected, joinRoom, leaveRoom, socket } from "../../components/Socket/socket";
import {
  getMessagePreviewText,
  getStatusMessageLabel,
  isStatusStyleMessage,
} from "./messageDisplay";


const ENABLE_GROUP_CHAT = true;

const getMemberDisplayName = (m) => {
  if (!m) return "";
  return (
    m?.candidate?.name ||
    m?.name ||
    `${m?.firstName || ""} ${m?.lastName || ""}`.trim() ||
    m?.email ||
    ""
  );
};

const isNamedMember = (m) => {
  const n = (getMemberDisplayName(m) || "").trim();
  return n.length > 0 && n.toLowerCase() !== "unknown";
};

const countNamedMembers = (members) => {
  if (!Array.isArray(members)) return 0;
  return members.filter(isNamedMember).length;
};

function campaignAdvertLabelFromAdvertId(advertId) {
  if (!advertId || typeof advertId !== "object") return "";
  const jobTitle = advertId.jobTitle?.name || "";
  const rawCampaign = advertId.campaignId;
  const campaignName = (Array.isArray(rawCampaign) ? rawCampaign[0]?.name : rawCampaign?.name) || "";
  if (campaignName && jobTitle) return `${campaignName} - ${jobTitle}`;
  if (campaignName) return campaignName;
  if (jobTitle) return jobTitle;
  return "";
}

function getGlobalSearchRoomRole(result, existing) {
  if (existing?.role) return existing.role;
  const campaignName = result?.campaignName || "";
  const advertName = result?.advertName || "";
  if (campaignName && advertName) return `${campaignName} - ${advertName}`;
  if (campaignName) return campaignName;
  if (advertName) return advertName;
  return campaignAdvertLabelFromAdvertId(result?.advertId);
}

function isRoomArchived(result, existing) {
  return Boolean(result?.isArchive ?? existing?.isArchive);
}

function getSearchRoomDisplayName(searchRoom) {
  if (!searchRoom) return "";
  const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
  const isGroupChat = searchRoom.type === "Group" || searchRoom.type === "group";
  if (isGroupChat) return searchRoom.title || searchRoom.name || "Group Chat";

  const members = searchRoom.members || [];
  const otherMember = members.find(
    (member) => String(member._id || member.id) !== String(currentUserId)
  );
  const rawName =
    otherMember?.candidate?.name ||
    otherMember?.name ||
    `${otherMember?.firstName || ""} ${otherMember?.lastName || ""}`.trim() ||
    otherMember?.email ||
    "";
  return rawName.trim();
}

const ArchivedBadge = () => (
  <span
    style={{
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      borderRadius: "20px",
      backgroundColor: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FCD34D",
      fontSize: "11px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      lineHeight: "16px",
    }}
  >
    Archived
  </span>
);

const ChatSidebar = ({chatRoomsResponse,chats,setChats,loadingChats,fetchChatRooms,formatTimeAgo,getProfileImageUrl, onChatSelect, selectedChat, showArchived, onArchiveClick, allChats, setAllChats, globalSearchResults = { rooms: [], messages: [] }, globalSearchLoading = false, isGlobalSearch = false, globalSearchQuery = "" }) => {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState("all");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [chatPage, setChatPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1700 : false
  );
  const chatScrollContainerRef = useRef(null);
  const chatScrollTimeoutRef = useRef(null);
  const fetchingChatsRef = useRef(false);
  const chatPageLimit = 20;
  const pendingChatSelectionRef = useRef(null);
  const selectedChatRef = useRef(null);
  const lastMessageTimestampsRef = useRef({}); // Track last message timestamp per chat to prevent double counting
  
  // Keep selectedChat ref in sync
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  // Track viewport so chat names can wrap instead of truncating on small screens
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1700);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Update last message timestamps when chats are loaded/updated
  useEffect(() => {
    if (chats && Array.isArray(chats)) {
      chats.forEach((chat) => {
        const chatId = chat.id || chat.roomId;
        if (chatId && chat.lastMessageTime) {
          const currentTimestamp = lastMessageTimestampsRef.current[chatId];
          const messageTime = new Date(chat.lastMessageTime).getTime();
          // Only update if this is a newer message than what we've tracked
          if (!currentTimestamp || messageTime > currentTimestamp) {
            lastMessageTimestampsRef.current[chatId] = messageTime;
          }
        }
      });
    }
  }, [chats]);
  
  // Reset pagination when showArchived changes
  useEffect(() => {
    setChatPage(1);
    setHasMoreChats(true);
    fetchingChatsRef.current = false;
    setLoadingMoreChats(false);
  }, [showArchived]);

  // Auto-load all chats while a candidate search query is active so the filter
  // can match against chats that haven't been paginated in yet.
  // Skip during chat/message global search — results come directly from the API.
  useEffect(() => {
    const isSearching = !!(globalSearchQuery && globalSearchQuery.trim().length > 0);
    if (!isSearching || isGlobalSearch) return;
    if (!hasMoreChats) return;
    if (fetchingChatsRef.current || loadingMoreChats || loadingChats) return;
    if (typeof fetchChatRooms !== "function") return;

    fetchingChatsRef.current = true;
    setLoadingMoreChats(true);

    const nextPage = chatPage + 1;

    fetchChatRooms(showArchived, nextPage, chatPageLimit, true)
      .then((result) => {
        if (result && result.hasMore) {
          setHasMoreChats(true);
          setChatPage(nextPage);
        } else {
          setHasMoreChats(false);
          if (result) setChatPage(nextPage);
        }
      })
      .catch((error) => {
        console.error("Error auto-loading chats for search:", error);
        setHasMoreChats(false);
      })
      .finally(() => {
        fetchingChatsRef.current = false;
        setLoadingMoreChats(false);
      });
  }, [globalSearchQuery, hasMoreChats, chatPage, loadingMoreChats, loadingChats, showArchived, fetchChatRooms, isGlobalSearch]);

  // Function to scroll selected chat to top
  const scrollChatToTop = (chatId) => {
    if (!chatId || !chatScrollContainerRef.current) return;
    
    setTimeout(() => {
      const container = chatScrollContainerRef.current;
      if (!container) return;
      
      // Find the chat card element by data attribute or by finding the card with matching key
      const chatCards = container.querySelectorAll('[data-chat-id]');
      let targetCard = null;
      
      for (let card of chatCards) {
        if (card.getAttribute('data-chat-id') === chatId) {
          targetCard = card;
          break;
        }
      }
      
      if (targetCard) {
        const containerRect = container.getBoundingClientRect();
        const cardRect = targetCard.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const cardTop = cardRect.top - containerRect.top + scrollTop;
        
        container.scrollTo({
          top: cardTop - 15, // 15px padding from top
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Effect to handle pending chat selection after list updates
  useEffect(() => {
    if (pendingChatSelectionRef.current && chats.length > 0) {
      const { candidateUserId, advertId, isGroup } = pendingChatSelectionRef.current;
      
      let chatToSelect;
      if (isGroup) {
        chatToSelect = chats.find((chat) => {
          return chat.isGroup && chat.advertId === advertId && 
                 chat.members?.some((m) => m._id === candidateUserId);
        });
      } else {
        chatToSelect = chats.find((chat) => {
          return chat.otherMemberId === candidateUserId && chat.advertId === advertId;
        });
      }
      
      if (chatToSelect) {
        const timeoutId = setTimeout(() => {
          setSelectedIndex(chatToSelect.id);
          if (onChatSelect) {
            onChatSelect(chatToSelect);
          }
          handleJoinRoom(chatToSelect.id);
          
          const roomId = chatToSelect.roomId || chatToSelect.id;
          window.dispatchEvent(new CustomEvent('chatSelected', {
            detail: { roomId: roomId, chatId: chatToSelect.id }
          }));
          
          const focusTextArea = () => {
            const textArea = document.querySelector('textarea[placeholder*="Type your message"]') || 
                            document.querySelector('.chat-textarea') ||
                            document.querySelector('textarea');
            if (textArea) {
              textArea.focus();
              textArea.click();
            }
          };
          
          setTimeout(focusTextArea, 300);
          setTimeout(focusTextArea, 600);
          setTimeout(focusTextArea, 1000);
          
          pendingChatSelectionRef.current = null;
        }, 150);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [chats, onChatSelect]);

  // Effect to handle member added from private chat - keep chat selected and focused
  useEffect(() => {
    const matchesRoom = (chat, roomId) =>
      chat.id === roomId || chat.roomId === roomId || chat._id === roomId;

    const updateChatMembers = (roomId, updater) => {
      const updateList = (prev) =>
        prev.map((c) => {
          if (!matchesRoom(c, roomId)) return c;
          const nextMembers = updater(Array.isArray(c.members) ? c.members : []);
          return {
            ...c,
            members: nextMembers,
            memberCount: countNamedMembers(nextMembers),
          };
        });
      if (typeof setChats === "function") setChats(updateList);
      if (typeof setAllChats === "function") setAllChats(updateList);
    };

    const handleMemberAdded = (event) => {
      const { roomId, memberId, member } = event.detail || {};
      if (!roomId) return;

      if (memberId || member) {
        updateChatMembers(roomId, (members) => {
          const idToMatch = String(memberId || member?._id || "");
          const alreadyExists = members.some((m) => String(m?._id) === idToMatch);
          if (alreadyExists || !idToMatch) return members;
          return [...members, member || { _id: idToMatch }];
        });
      }
    };

    const handleMemberRemoved = (event) => {
      const { roomId, memberId } = event.detail || {};
      if (!roomId || !memberId) return;
      updateChatMembers(roomId, (members) =>
        members.filter((m) => String(m?._id) !== String(memberId))
      );
    };
    
    window.addEventListener('memberAddedToChat', handleMemberAdded);
    window.addEventListener('memberRemovedFromChat', handleMemberRemoved);
    return () => {
      window.removeEventListener('memberAddedToChat', handleMemberAdded);
      window.removeEventListener('memberRemovedFromChat', handleMemberRemoved);
    };
  }, [chats, onChatSelect, setChats, setAllChats]);

  // Effect to scroll when selectedChat or selectedIndex changes - REMOVED per user request
  // useEffect(() => {
  //   const chatId = selectedChat?.id || selectedIndex;
  //   if (chatId) {
  //     scrollChatToTop(chatId);
  //   }
  // }, [selectedChat?.id, selectedIndex]);

  const [startChatModalVisible, setStartChatModalVisible] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [chatMode, setChatMode] = useState(ENABLE_GROUP_CHAT ? "group" : "private");
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const pageLimit = 10;
  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const fetchingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const tabItems = [
    { key: "all", label: "All" },
    { key: "unread", label: "Unread" },
    { key: "read", label: "Read" },
  ];

  // Archive handler
  const handleArchive = async (chat) => {
    const roomId = chat.roomId || chat.id;
    if (!roomId) {
      message.error("Room ID not found.");
      return;
    }

    try {
      await API_MANAGER.archiveChatRoom(roomId);
      message.success("Chat successfully archived.");
      
      // Remove chat from list
      setChats((prev) => prev.filter((c) => c.id !== chat.id));
      
      // Clear selection if this chat was selected
      if (selectedChat?.id === chat.id) {
        setSelectedIndex(null);
        if (onChatSelect) {
          onChatSelect(null);
        }
      } else {
        setSelectedIndex(null);
      }
    } catch (error) {
      console.error("Failed to archive chat:", error);
      message.error(
        error?.response?.data?.message || "Failed to archive chat. Please try again."
      );
    }
  };

  // Unarchive handler (uses same API as archive)
  const handleUnarchive = async (chat) => {
    const roomId = chat.roomId || chat.id;
    if (!roomId) {
      message.error("Room ID not found.");
      return;
    }

    try {
      await API_MANAGER.archiveChatRoom(roomId);
      message.success("Chat successfully unarchived.");
      
      // Remove chat from list
      setChats((prev) => prev.filter((c) => c.id !== chat.id));
      
      // Clear selection if this chat was selected
      if (selectedChat?.id === chat.id) {
        setSelectedIndex(null);
        if (onChatSelect) {
          onChatSelect(null);
        }
      } else {
        setSelectedIndex(null);
      }
    } catch (error) {
      console.error("Failed to unarchive chat:", error);
      message.error(
        error?.response?.data?.message || "Failed to unarchive chat. Please try again."
      );
    }
  };

  // Leave room via WebSocket
  const handleLeaveRoom = (roomId) => {
    if (!roomId) return;
    leaveRoom(roomId);
    const storedRoomId = sessionStorage.getItem("leaveroom");
    if (storedRoomId === roomId) {
      sessionStorage.removeItem("leaveroom");
    }
  };

  // Ref to store socket for cleanup without causing re-renders
  const socketRef = useRef(socket);
  
  // Keep socketRef in sync
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  // Clear session storage and leave room on navigation/unmount
  useEffect(() => {
    const handleBeforeUnload = () => {
      const storedRoomId = sessionStorage.getItem("leaveroom");
      if (storedRoomId) {
        leaveRoom(storedRoomId);
        sessionStorage.removeItem("leaveroom");
      }
    };

    const handleNavigation = () => {
      const storedRoomId = sessionStorage.getItem("leaveroom");
      if (storedRoomId) {
        leaveRoom(storedRoomId);
        sessionStorage.removeItem("leaveroom");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("chatRouteChange", handleNavigation);

    return () => {
      const storedRoomId = sessionStorage.getItem("leaveroom");
      if (storedRoomId) {
        leaveRoom(storedRoomId);
        sessionStorage.removeItem("leaveroom");
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("chatRouteChange", handleNavigation);
    };
  }, []);

  // Join room via WebSocket (deduped in socket.js)
  const handleJoinRoom = (roomId) => {
    if (!roomId) return;

    const storedRoomId = sessionStorage.getItem("leaveroom");
    if (storedRoomId && storedRoomId !== roomId) {
      leaveRoom(storedRoomId);
    }

    ensureSocketConnected();
    joinRoom(roomId);
    sessionStorage.setItem("leaveroom", roomId);
  };

  // Listen for message-notify events to update unread counts in real-time
  useEffect(() => {
    if (!socket) {
      return;
    }

    const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
    if (!currentUserId) {
      return;
    }

    const handleMessageNotify = (notificationData) => {
      try {
        console.log("📩 message-notify received:", notificationData);
        
        // Extract roomId from notification
        const messageRoomId = notificationData?.roomId;
        
        console.log("📩 message-notify roomId:", messageRoomId);
        
        if (!messageRoomId) {
          console.warn("⚠️ message-notify received but no roomId found:", notificationData);
          return;
        }

        // Get currently selected chat
        const currentSelectedChat = selectedChatRef.current;
        const selectedChatId = currentSelectedChat?.id || currentSelectedChat?.roomId;
        const isSelectedChat = String(selectedChatId) === String(messageRoomId);
        
        console.log("📩 message-notify comparison:", {
          messageRoomId,
          selectedChatId,
          isSelectedChat,
          currentSelectedChat: currentSelectedChat?.name || 'none'
        });

        // Only increment unread count if this chat is NOT currently selected
        if (!isSelectedChat) {
          console.log("📩 message-notify: Chat is NOT selected, will update unread count");
          // Get message timestamp from notification
          const messageTime = notificationData?.createdAt || notificationData?.timestamp;
          const messageTimestamp = messageTime ? new Date(messageTime).getTime() : Date.now();
          
          // Check if this message was already processed (prevent double counting)
          const roomIdStr = String(messageRoomId);
          const lastSeenTimestamp = lastMessageTimestampsRef.current[roomIdStr] || 0;
          const isNewMessage = messageTimestamp > lastSeenTimestamp;
          
          console.log("📩 message-notify: Checking if new message:", {
            roomId: roomIdStr,
            messageTimestamp,
            lastSeenTimestamp,
            isNewMessage
          });
          
          if (!isNewMessage) {
          
            return;
          }
          
          // Mark this message as processed BEFORE updating state
          lastMessageTimestampsRef.current[roomIdStr] = messageTimestamp;
          
          // Helper function to update chat unread count (only increments, no timestamp check needed)
          const updateChatUnreadCount = (chat) => {
            const chatId = String(chat.id || chat.roomId || "");
            
            if (chatId === roomIdStr || 
                String(chat.id) === roomIdStr ||
                String(chat.roomId) === roomIdStr) {
              
              // Increment unread count - ensure it's always a number
              const currentUnreadCount = typeof chat.unreadCount === 'number' ? chat.unreadCount : 0;
              const newUnreadCount = currentUnreadCount + 1;
              
              console.log("📩 message-notify: Incrementing unread count:", {
                chatId,
                chatName: chat.name,
                currentUnreadCount,
                newUnreadCount
              });
              
              // Update chat with new unread count
              const updatedChat = {
                ...chat,
                unreadCount: newUnreadCount,
              };

              // Update message preview and time if available
              if (notificationData?.message) {
                updatedChat.message = notificationData.message;
              }
              if (messageTime) {
                updatedChat.time = formatTimeAgo ? formatTimeAgo(messageTime) : chat.time;
                updatedChat.lastMessageTime = messageTime;
                updatedChat.updatedAt = messageTime;
              }

              return updatedChat;
            }
            return chat;
          };

          // Update allChats first (source of truth) - this ensures the count persists
          if (setAllChats && allChats && Array.isArray(allChats)) {
            setAllChats((prevAllChats) => {
              const updated = prevAllChats.map(updateChatUnreadCount);
              return updated;
            });
          }

          // Update chats (filtered list) - this ensures the badge shows immediately
          setChats((prevChats) => {
            const updated = prevChats.map(updateChatUnreadCount);
            return updated;
          });
        } else {
          console.log("📩 message-notify: Chat IS currently selected, skipping unread count update");
        }
      } catch (error) {
        console.error("❌ Error in message-notify handler:", error);
      }
    };

    // Add listener for message-notify event
    socket.on("message-notify", handleMessageNotify);

    // Cleanup on unmount
    return () => {
      socket.off("message-notify", handleMessageNotify);
    };
  }, [socket, formatTimeAgo, setChats]);

  // Initialize socket connection and add event listeners for debugging
  useEffect(() => {
    if (!socket) {
      console.error("❌ Socket instance not available");
      return;
    }

    console.log("🔌 ChatSidebar: Checking socket connection...");
    console.log("📡 Socket connection status:", socket.connected ? "✅ Connected" : "❌ Not connected");
    console.log("📡 Socket ID:", socket.id || "Not connected yet");
    console.log("📡 Socket connecting:", socket.connecting ? "⏳ Yes" : "❌ No");
    
    // Check if userId is available
    const userId = localStorage.getItem("userId") || localStorage.getItem("userID");
    
    console.log("🔐 Authentication check:", {
      userId: userId ? "✅ Present" : "❌ Missing",
    });

    if (!userId) {
      console.warn("⚠️ userId not found. Socket will not connect.");
      return;
    }

    // Add connection event listeners for debugging (these are already in socket.js, but adding here for component-specific logging)
    const handleConnect = () => {
      console.log("✅ ChatSidebar: Socket connected! Socket ID:", socket.id);
      console.log("📡 Transport:", socket.io?.engine?.transport?.name);
    };

    const handleDisconnect = (reason) => {
      console.warn("⚠️ ChatSidebar: Socket disconnected. Reason:", reason);
    };

    const handleConnectError = (error) => {
      console.error("❌ ChatSidebar: Socket connection error:", error);
      console.error("Error details:", {
        message: error.message,
        description: error.description,
        type: error.type,
        data: error.data,
      });
      
      // Log the actual connection URL being attempted
      if (socket.io) {
        console.error("🔗 Connection URL:", socket.io.uri);
        console.error("🔗 Connection path:", socket.io.opts?.path);
      }
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    // Check if userId is available before attempting connection
    if (!userId) {
      console.warn("⚠️ userId not found. Socket will not connect.");
      return;
    }

    // Try to connect if not connected and not already connecting
    if (!socket.connected && !socket.connecting) {
      console.log("🔄 Attempting to connect socket...");
      ensureSocketConnected();
    } else if (socket.connecting) {
      console.log("⏳ Socket is already connecting...");
    } else if (socket.connected) {
      console.log("✅ Socket is already connected!");
    }

    // Cleanup on unmount
    return () => {
      // Remove listeners to prevent memory leaks
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      // Don't disconnect socket as it might be used by other components
      // socket.disconnect();
    };
  }, []);

  const fetchApplications = useCallback(async (page = 1, search = "", reset = false, modeOverride) => {
    if (loadingRef.current || fetchingRef.current) return;
    
    loadingRef.current = true;
    fetchingRef.current = true;
    setLoading(true);
    
    try {
      const activeMode = modeOverride || chatMode;
      const params = {
        page: page,
        limit: pageLimit,
        type: activeMode === "private" ? "PRIVATE" : "GROUP",
      };
      
      if (search) {
        params.search = search;
      }

      const response = await API_MANAGER.getCompanyApplications(params);
      let data = response?.data?.data?.result || response?.data?.result || response?.data?.data || response?.data || [];
      if (!Array.isArray(data)) {
        data = [data];
      }
      const total = response?.data?.data?.totalCount || response?.data?.totalCount || response?.data?.totalRecords || response?.data?.total || data.length;

      if (reset) {
        setApplications(data);
        setHasMore(data.length >= pageLimit && data.length < total);
      } else {
        setApplications((prev) => {
          const newData = [...prev, ...data];
          const shouldHaveMore = data.length >= pageLimit && newData.length < total;
          setHasMore(shouldHaveMore);
          return newData;
        });
      }

      setTotalRecords(total);
      setCurrentPage(page);
    } catch (error) {
      message.error("Failed to load applications. Please try again.");
    } finally {
      setLoading(false);
      loadingRef.current = false;
      fetchingRef.current = false;
    }
  }, [pageLimit, chatMode]);

  useEffect(() => {
    if (startChatModalVisible) {
      setApplications([]);
      setCurrentPage(1);
      setHasMore(true);
      setSearchText("");
      loadingRef.current = false;
      fetchingRef.current = false;
      fetchChatRooms(showArchived, 1, chatPageLimit, false);
      fetchApplications(1, "", true, chatMode);
    }
  }, [startChatModalVisible]);

  useEffect(() => {
    if (!startChatModalVisible) return;
    setApplications([]);
    setCurrentPage(1);
    setHasMore(true);
    loadingRef.current = false;
    fetchingRef.current = false;
    fetchApplications(1, searchText, true, chatMode);
  }, [chatMode]);

  useEffect(() => {
    if (!startChatModalVisible) return;
    
    const timeoutId = setTimeout(() => {
      if (searchText) {
        setApplications([]);
        setCurrentPage(1);
        setHasMore(true);
        loadingRef.current = false;
        fetchingRef.current = false;
        fetchApplications(1, searchText, true, chatMode);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const handleScroll = useCallback((e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;
      
      if (isNearBottom && hasMore && !loadingRef.current && !fetchingRef.current) {
        fetchApplications(currentPage + 1, searchText, false, chatMode);
      }
    }, 200);
  }, [hasMore, currentPage, searchText, fetchApplications, chatMode]);

  const handleStartChatClick = () => {
    setStartChatModalVisible(true);
  };

  const handleModalClose = async () => {
    setStartChatModalVisible(false);
    setApplications([]);
    setSearchText("");
    setCurrentPage(1);
    setHasMore(true);
    loadingRef.current = false;
    fetchingRef.current = false;
    setChatMode(ENABLE_GROUP_CHAT ? "group" : "private");
    setSelectedGroupMembers([]);
    setCreatingGroup(false);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setChatPage(1);
    setHasMoreChats(true);
    await fetchChatRooms(showArchived, 1, chatPageLimit, false);
  };

  const handleCandidateClick = async (application) => {
    try {
      const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
      const candidateUserId = application?.profileId?.userId?._id;
      const advertId = application?.advertId?._id;
      const candidateName = application?.profileId?.name || application?.candidateName || "Unknown";

      if (!currentUserId || !candidateUserId || !advertId) {
        message.error("Missing required information to start chat.");
        return;
      }

      // First, check if PRIVATE chat already exists in the sidebar chat list
      const existingChatInList = chats.find((chat) => {
        const isPrivate = !chat.isGroup && chat.type !== "Group";
        const sameUser = String(chat.otherMemberId) === String(candidateUserId);
        const sameAdvert = String(chat.advertId) === String(advertId);
        return isPrivate && sameUser && sameAdvert;
      });

      if (existingChatInList) {
        // Chat already exists in sidebar
        // Switch to "all" tab to ensure chat is visible (in case it's filtered by unread/read)
        if (activeKey !== "all") {
          setActiveKey("all");
        }
        
        // Close modal first
        setStartChatModalVisible(false);
        setApplications([]);
        setSearchText("");
        setCurrentPage(1);
        setHasMore(true);
        loadingRef.current = false;
        fetchingRef.current = false;
        
        // Select the chat immediately to show selected state (yellow highlight)
        setSelectedIndex(existingChatInList.id);
        if (onChatSelect) {
          onChatSelect(existingChatInList);
        }
        handleJoinRoom(existingChatInList.id);
        
        // Dispatch event to notify Layout about selected chat
        const roomId = existingChatInList.roomId || existingChatInList.id;
        window.dispatchEvent(new CustomEvent('chatSelected', {
          detail: { roomId: roomId, chatId: existingChatInList.id }
        }));
        
        // Call API to update unread count and trigger sidebar menu update
        try {
          await API_MANAGER.getChatUnreadCount();
          window.dispatchEvent(new CustomEvent('chatUnreadCountUpdated'));
        } catch (error) {
          console.error("Failed to fetch unread count:", error);
        }
        
        pendingChatSelectionRef.current = { candidateUserId, advertId, isGroup: false };
        
        fetchChatRooms(showArchived, 1, chatPageLimit, false).then(() => {
          setTimeout(() => {
            setSelectedIndex(existingChatInList.id);
            if (onChatSelect) {
              onChatSelect(existingChatInList);
            }
          }, 100);
        });
        
        return;
      }

      loadingRef.current = true;
      fetchingRef.current = true;
      setLoading(true);
      
      const records = chatRoomsResponse?.data?.data?.[0]?.records || chatRoomsResponse?.data?.records || [];
      
      const existingRoom = records.find((room) => {
        const memberIds = room.members?.map((m) => m._id) || [];
        const hasBothMembers = memberIds.includes(currentUserId) && memberIds.includes(candidateUserId);
        const sameAdvert = room.advertId?._id === advertId;
        return hasBothMembers && sameAdvert && room.type === "Private";
      });

      if (existingRoom) {
        const otherMember = existingRoom.members?.find(
          (member) => member._id !== currentUserId
        );
        const candidate = otherMember?.candidate;
        const rawName = candidate?.name || `${otherMember?.firstName || ""} ${otherMember?.lastName || ""}`.trim() || otherMember?.email || "Unknown";
        const name = rawName.trim() || "Unknown";
        const profileImage = candidate?.profileImage || null;
        const jobTitle = existingRoom.advertId?.jobTitle?.name || "";
        const campaignName = existingRoom.advertId?.campaignId?.[0]?.name || "";
        let role = "";
        if (campaignName && jobTitle) {
          role = `${campaignName} - ${jobTitle}`;
        } else if (campaignName) {
          role = campaignName;
        } else if (jobTitle) {
          role = jobTitle;
        }
        const location = candidate 
          ? `${candidate.city || ""}, ${candidate.state || ""}, ${candidate.postalCode || ""}`.replace(/^,\s*|,\s*$/g, "").replace(/,\s*,/g, ",")
          : "";

        const existingChat = {
          id: existingRoom._id,
          name: name,
          role: role,
          location: location,
          message: "",
          time: formatTimeAgo(existingRoom.updatedAt),
          unreadCount: existingRoom.unreadCount || 0,
          avatarUrl: getProfileImageUrl(profileImage),
          roomId: existingRoom._id,
          otherMemberId: otherMember._id,
          advertId: existingRoom.advertId?._id,
        };

        // Close modal first
        setStartChatModalVisible(false);
        setApplications([]);
        setSearchText("");
        setCurrentPage(1);
        setHasMore(true);
        loadingRef.current = false;
        fetchingRef.current = false;
        
        // Select the chat immediately to show selected state (yellow highlight)
        setSelectedIndex(existingChat.id);
        if (onChatSelect) {
          onChatSelect(existingChat);
        }
        // Join room via WebSocket
        handleJoinRoom(existingChat.id);
        
        // Dispatch event to notify Layout about selected chat
        const roomId = existingChat.roomId || existingChat.id;
        window.dispatchEvent(new CustomEvent('chatSelected', {
          detail: { roomId: roomId, chatId: existingChat.id }
        }));
        
        // Call API to update unread count and trigger sidebar menu update
        try {
          await API_MANAGER.getChatUnreadCount();
          window.dispatchEvent(new CustomEvent('chatUnreadCountUpdated'));
        } catch (error) {
          console.error("Failed to fetch unread count:", error);
        }
        
        pendingChatSelectionRef.current = { candidateUserId, advertId, isGroup: false };
        
        fetchChatRooms(showArchived, 1, chatPageLimit, false).then(() => {
          setTimeout(() => {
            setSelectedIndex(existingChat.id);
            if (onChatSelect) {
              onChatSelect(existingChat);
            }
          }, 100);
        });
      } else {
        const chatData = {
          members: [currentUserId, candidateUserId],
          advertId: advertId,
          type: "Private"
        };

        const createResponse = await API_MANAGER.createChat(chatData);

        if (createResponse?.data?.isSuccess === false) {
          const backendMsg = createResponse?.data?.message;
          const text =
            typeof backendMsg === "string"
              ? backendMsg
              : backendMsg?.message || "Failed to start chat.";
          throw new Error(text);
        }

        setStartChatModalVisible(false);
        setApplications([]);
        setSearchText("");
        setCurrentPage(1);
        setHasMore(true);
        loadingRef.current = false;
        fetchingRef.current = false;

        pendingChatSelectionRef.current = { candidateUserId, advertId, isGroup: false };

        await fetchChatRooms(showArchived, 1, chatPageLimit, false);

        let newRoom = createResponse?.data?.data || createResponse?.data;

        if (!newRoom || !newRoom._id) {
          const updatedRoomsResponse = await API_MANAGER.getChatRooms();
          const updatedRecords = updatedRoomsResponse?.data?.data?.[0]?.records || updatedRoomsResponse?.data?.records || [];
          newRoom = updatedRecords.find((room) => {
            const memberIds = room.members?.map((m) => m._id) || [];
            const isPrivateRoom = !room.isGroup && room.type !== "Group";
            return memberIds.includes(currentUserId) && memberIds.includes(candidateUserId) && room.advertId?._id === advertId && isPrivateRoom;
          });
        }

        if (newRoom && newRoom._id && onChatSelect) {
          const otherMember = newRoom.members?.find(
            (member) => member._id !== currentUserId
          );
          const candidate = otherMember?.candidate;
          const rawName = candidate?.name || `${otherMember?.firstName || ""} ${otherMember?.lastName || ""}`.trim() || otherMember?.email || candidateName || "Unknown";
          const name = rawName.trim() || "Unknown";
          const profileImage = candidate?.profileImage || null;
          const jobTitle = newRoom.advertId?.jobTitle?.name || "";
          const campaignName = newRoom.advertId?.campaignId?.[0]?.name || newRoom.advertId?.campaignId?.name || "";
          let role = "";
          if (campaignName && jobTitle) {
            role = `${campaignName} - ${jobTitle}`;
          } else if (campaignName) {
            role = campaignName;
          } else if (jobTitle) {
            role = jobTitle;
          }
          const location = candidate 
            ? `${candidate.city || ""}, ${candidate.state || ""}, ${candidate.postalCode || ""}`.replace(/^,\s*|,\s*$/g, "").replace(/,\s*,/g, ",")
            : "";

          const newChat = {
            id: newRoom._id,
            name: name,
            role: role,
            location: location,
            message: "",
            time: formatTimeAgo(newRoom.updatedAt || new Date()),
            lastMessageTime: newRoom.updatedAt || new Date().toISOString(),
            updatedAt: newRoom.updatedAt || new Date().toISOString(),
            unreadCount: newRoom.unreadCount || 0,
            avatarUrl: getProfileImageUrl(profileImage),
            roomId: newRoom._id,
            otherMemberId: otherMember?._id || candidateUserId,
            advertId: newRoom.advertId?._id || advertId,
            type: "Private",
            isGroup: false,
            members: newRoom.members || [],
          };

          if (typeof setAllChats === "function") {
            setAllChats((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              if (list.some((c) => c.id === newRoom._id || c.roomId === newRoom._id)) {
                return list;
              }
              return [newChat, ...list];
            });
          }

          setSelectedIndex(newChat.id);
          onChatSelect(newChat);
          // Join room via WebSocket
          handleJoinRoom(newChat.id);
          
          // Dispatch event to notify Layout about selected chat
          window.dispatchEvent(new CustomEvent('chatSelected', {
            detail: { roomId: newChat.id, chatId: newChat.id }
          }));
          
          setTimeout(() => {
            const textArea = document.querySelector('textarea[placeholder*="Type your message"]');
            if (textArea) {
              textArea.focus();
            }
          }, 500);
          
          // Call API to update unread count and trigger sidebar menu update
          try {
            await API_MANAGER.getChatUnreadCount();
            window.dispatchEvent(new CustomEvent('chatUnreadCountUpdated'));
          } catch (err) {
            console.error("Failed to fetch unread count:", err);
          }
        }
      }
    } catch (error) {
      loadingRef.current = false;
      fetchingRef.current = false;
      setLoading(false);
      const errMsg = error?.response?.data?.message || error?.message;
      message.error(
        typeof errMsg === "string"
          ? errMsg
          : errMsg?.message || "Failed to start chat. Please try again."
      );
    } finally {
      loadingRef.current = false;
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  const handleGroupCandidateSelect = async (application) => {
    const memberId = application?.profileId?.userId?._id;
    const advertId = application?.advertId?._id;
    const candidateName = application?.profileId?.name || application?.candidateName || "Group";

    if (!memberId || !advertId) {
      message.error("Missing required information to create group.");
      return;
    }

    const candidateIdCandidates = [
      String(memberId || ""),
      String(application?.profileId?._id || ""),
      String(application?.profileId?.candidate?._id || ""),
      String(application?.candidate?._id || ""),
      String(application?.userId?._id || application?.userId || ""),
    ].filter(Boolean);

    const isCandidateMember = (m) => {
      if (!m) return false;
      if (typeof m === "string") return candidateIdCandidates.includes(m);
      const possibleIds = [
        String(m._id || ""),
        String(m.id || ""),
        String(m.userId?._id || m.userId || ""),
        String(m.user?._id || m.user || ""),
        String(m.profileId?._id || m.profileId || ""),
        String(m.profileId?.userId?._id || m.profileId?.userId || ""),
        String(m.candidate?._id || ""),
        String(m.candidate?.userId?._id || m.candidate?.userId || ""),
      ].filter(Boolean);
      return possibleIds.some((id) => candidateIdCandidates.includes(id));
    };

    const chatList = Array.isArray(allChats) && allChats.length > 0
      ? allChats
      : (Array.isArray(chats) ? chats : []);

    const existingGroupChat = chatList.find((chat) => {
      const isGroup = chat.isGroup === true || chat.type === "Group" || chat.type === "group";
      if (!isGroup) return false;
      if (String(chat.advertId || "") !== String(advertId)) return false;
      const members = Array.isArray(chat.members) ? chat.members : [];
      return members.some(isCandidateMember);
    });

    if (existingGroupChat) {
      if (activeKey !== "all") {
        setActiveKey("all");
      }

      setStartChatModalVisible(false);
      setApplications([]);
      setSearchText("");
      setCurrentPage(1);
      setHasMore(true);
      loadingRef.current = false;
      fetchingRef.current = false;
      setChatMode("group");
      setSelectedGroupMembers([]);

      setSelectedIndex(existingGroupChat.id);
      if (onChatSelect) {
        onChatSelect(existingGroupChat);
      }
      handleJoinRoom(existingGroupChat.id);

      const existingRoomId = existingGroupChat.roomId || existingGroupChat.id;
      window.dispatchEvent(new CustomEvent('chatSelected', {
        detail: { roomId: existingRoomId, chatId: existingGroupChat.id }
      }));

      try {
        await API_MANAGER.getChatUnreadCount();
        window.dispatchEvent(new CustomEvent('chatUnreadCountUpdated'));
      } catch (err) {
        console.error("Failed to fetch unread count:", err);
      }

      message.info("Group chat already exists for this candidate.");
      return;
    }

    setCreatingGroup(true);
    let chatCreated = false;
    let createResponse = null;
    
    try {
      const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
      const memberIds = [currentUserId, memberId];

      const chatData = {
        members: memberIds,
        type: "Group",
        title: candidateName,
        advertId: advertId,
      };

      createResponse = await API_MANAGER.createChat(chatData);

      if (createResponse?.data?.isSuccess === false) {
        const backendMsg = createResponse?.data?.message;
        const text =
          typeof backendMsg === "string"
            ? backendMsg
            : backendMsg?.message || "Failed to create group chat.";
        throw new Error(text);
      }

      chatCreated = true;

      const newRoomFromCreate = createResponse?.data?.data || createResponse?.data;

      if (newRoomFromCreate?._id && typeof setAllChats === "function") {
        const candidateProfile = application?.profileId;
        const advertCampaignRaw = application?.advertId?.campaignId || newRoomFromCreate?.advertId?.campaignId;
        const advertCampaignName = (Array.isArray(advertCampaignRaw) ? advertCampaignRaw[0]?.name : advertCampaignRaw?.name) || "";
        const advertJobTitleName = application?.advertId?.jobTitle?.name || newRoomFromCreate?.advertId?.jobTitle?.name || "";
        let optimisticRole = "";
        if (advertCampaignName && advertJobTitleName) optimisticRole = `${advertCampaignName} - ${advertJobTitleName}`;
        else if (advertCampaignName) optimisticRole = advertCampaignName;
        else if (advertJobTitleName) optimisticRole = advertJobTitleName;

        const fallbackMembers = [
          { _id: currentUserId },
          {
            _id: memberId,
            firstName: candidateProfile?.firstName,
            lastName: candidateProfile?.lastName,
            candidate: candidateProfile,
          },
        ];
        const optimisticMembers = Array.isArray(newRoomFromCreate.members) && newRoomFromCreate.members.length > 0
          ? newRoomFromCreate.members
          : fallbackMembers;

        const nowIso = new Date().toISOString();
        const safeFormatTimeAgo = typeof formatTimeAgo === "function" ? formatTimeAgo : () => "Just now";
        const optimisticChat = {
          id: newRoomFromCreate._id,
          name: newRoomFromCreate.title || candidateName,
          role: optimisticRole,
          location: "",
          message: "",
          time: safeFormatTimeAgo(newRoomFromCreate.updatedAt || nowIso),
          lastMessageTime: newRoomFromCreate.updatedAt || nowIso,
          updatedAt: newRoomFromCreate.updatedAt || nowIso,
          unreadCount: 0,
          avatarUrl: null,
          roomId: newRoomFromCreate._id,
          otherMemberId: null,
          advertId: newRoomFromCreate.advertId?._id || advertId,
          statusLabel: "INVITED",
          type: "Group",
          isGroup: true,
          memberCount: countNamedMembers(optimisticMembers),
          members: optimisticMembers,
        };

        const upsertAtTop = (prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const filtered = list.filter((c) => c.id !== optimisticChat.id && c.roomId !== optimisticChat.id);
          return [optimisticChat, ...filtered];
        };
        setAllChats(upsertAtTop);
        if (typeof setChats === "function") setChats(upsertAtTop);
      }

      setStartChatModalVisible(false);
      setApplications([]);
      setSearchText("");
      setCurrentPage(1);
      setHasMore(true);
      loadingRef.current = false;
      fetchingRef.current = false;
      setChatMode("group");
      setSelectedGroupMembers([]);

      pendingChatSelectionRef.current = { candidateUserId: memberId, advertId, isGroup: true };

      if (typeof fetchChatRooms === "function") {
        try {
          await fetchChatRooms(showArchived, 1, chatPageLimit, false);
        } catch (refreshErr) {
          console.error("Failed to refresh chat rooms after group creation:", refreshErr);
        }
      }

      message.success("Group chat successfully created.");
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message;
      message.error(
        typeof errMsg === "string"
          ? errMsg
          : errMsg?.message || "Failed to create group chat."
      );
      setCreatingGroup(false);
      return;
    }

    try {
      let newRoom = createResponse?.data?.data || createResponse?.data;

      if (!newRoom || !newRoom._id) {
        const updatedRoomsResponse = await API_MANAGER.getChatRooms();
        const updatedRecords = updatedRoomsResponse?.data?.data?.[0]?.records || updatedRoomsResponse?.data?.records || [];
        newRoom = updatedRecords.find((room) => {
          const isGroupRoom = room.isGroup || room.type === "Group";
          const matchesAdvert = room.advertId?._id === advertId;
          const hasMember = room.members?.some((m) => m._id === memberId);
          return isGroupRoom && matchesAdvert && hasMember;
        });
      }

      if (newRoom && newRoom._id && onChatSelect) {
        const jobTitle = newRoom.advertId?.jobTitle?.name || "";
        const campaignName = newRoom.advertId?.campaignId?.[0]?.name || newRoom.advertId?.campaignId?.name || "";
        let role = "";
        if (campaignName && jobTitle) {
          role = `${campaignName} - ${jobTitle}`;
        } else if (campaignName) {
          role = campaignName;
        } else if (jobTitle) {
          role = jobTitle;
        }

        const newChat = {
          id: newRoom._id,
          name: newRoom.title || candidateName,
          role: role,
          location: "",
          message: "",
          time: formatTimeAgo(newRoom.updatedAt || new Date()),
          unreadCount: newRoom.unreadCount || 0,
          avatarUrl: null,
          roomId: newRoom._id,
          advertId: newRoom.advertId?._id || advertId,
          isGroup: true,
          members: newRoom.members || [],
        };

        setSelectedIndex(newChat.id);
        onChatSelect(newChat);
        handleJoinRoom(newChat.id);
        
        window.dispatchEvent(new CustomEvent('chatSelected', {
          detail: { roomId: newChat.id, chatId: newChat.id }
        }));
        
        const focusTextArea = () => {
          const textArea = document.querySelector('textarea[placeholder*="Type your message"]') || 
                          document.querySelector('.chat-textarea') ||
                          document.querySelector('textarea');
          if (textArea) {
            textArea.focus();
            textArea.click();
          }
        };
        
        setTimeout(focusTextArea, 300);
        setTimeout(focusTextArea, 600);
        setTimeout(focusTextArea, 1000);
        
        try {
          await API_MANAGER.getChatUnreadCount();
          window.dispatchEvent(new CustomEvent('chatUnreadCountUpdated'));
        } catch (err) {
          console.error("Failed to fetch unread count:", err);
        }
      } else {
        pendingChatSelectionRef.current = { candidateUserId: memberId, advertId, isGroup: true };
      }
    } catch (err) {
      console.error("Error activating group chat:", err);
      pendingChatSelectionRef.current = { candidateUserId: memberId, advertId, isGroup: true };
    } finally {
      setCreatingGroup(false);
    }
  };

  const getLocationDetails = (application) => {
    const campaign = application?.advertId?.campaignId;
    
    let city = "";
    let state = "";
    let postalCode = "";
    
    if (campaign?.suburb) city = campaign.suburb;
    if (campaign?.state?.name) state = campaign.state.name;
    if (campaign?.postalCode) postalCode = campaign.postalCode;
    
    const locationParts = [];
    if (city) locationParts.push(city);
    if (state) locationParts.push(state);
    if (postalCode) locationParts.push(postalCode);
    
    return locationParts.length > 0 ? locationParts.join(", ") : null;
  };

  const getDisplayText = (application) => {
    const campaignName = application?.advertId?.campaignId?.name || "";
    const jobTitle = application?.advertId?.jobTitle?.name || "";
    
    if (campaignName && jobTitle) {
      return `${campaignName} - ${jobTitle}`;
    } else if (campaignName) {
      return campaignName;
    } else if (jobTitle) {
      return jobTitle;
    }
    return "N/A";
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
      {/* Archive List Button - Top Left - Only show when in archive list */}
      {showArchived && (
        <div style={{ padding: "15px", paddingBottom: "10px" }}>
          
            <Button
              onClick={onArchiveClick}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                background: "#091E69",
                border: "3px solid #F9CF66",
                borderRadius: "8px",
                padding: "20px 12px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#FFFFFF",
                cursor: "pointer",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                // height: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "22px",
                  height: "22px",
                  background: "#EEF4FF",
                  borderRadius: "6px",
                }}
              >
                <img src={LeftArrow} alt="Back" style={{ width: "12px", height: "12px" }} />
              </div>
              Back to Active List
            </Button>
      
        </div>
      )}

      {/* Tabs - Only show when not in archive list */}
      {!showArchived && (
        <div
          style={{
            marginTop: "10px",
            border: "1px solid #E4E8ED",
            borderRadius: "12px",
            marginRight: "10px",
            marginLeft: "10px",
            flexShrink: 0,
          }}
        >
          <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            renderTabBar={(props, DefaultTabBar) => (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  background: "#F5F6FA",
                  borderRadius: "12px",
                  padding: "1px",
                  justifyContent: "space-between",
                }}
              >
                {tabItems.map((tab) => (
                  <div
                    key={tab.key}
                    onClick={() => setActiveKey(tab.key)}
                    style={{
                      borderRadius: "8px",
                      padding: "10px 20px",
                      fontWeight: 600,
                      background:
                        activeKey === tab.key ? "#0A1444" : "transparent",
                      color: activeKey === tab.key ? "#fff" : "#091647",
                      cursor: "pointer",
                      width: "100px",
                      height: "36px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>
            )}
            items={tabItems.map((tab) => ({
              key: tab.key,
              label: tab.label,
            }))}
          />
        </div>
      )}

      {/* Chat List */}
      <div 
        ref={chatScrollContainerRef}
        onScroll={(e) => {
          // Early return if already fetching or no more chats
          if (fetchingChatsRef.current || loadingMoreChats || loadingChats || !hasMoreChats) {
            return;
          }
          
          // Clear existing timeout
          if (chatScrollTimeoutRef.current) {
            clearTimeout(chatScrollTimeoutRef.current);
          }

          chatScrollTimeoutRef.current = setTimeout(() => {
            // Double check guards before making API call
            if (fetchingChatsRef.current || loadingMoreChats || loadingChats || !hasMoreChats) {
              return;
            }
            
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            
            // Only trigger if there's actual scrollable content
            if (scrollHeight <= clientHeight) {
              return; // No scroll needed, all content visible
            }
            
            // Calculate scroll percentage
            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
            const isNearBottom = scrollPercentage >= 0.9; // Only trigger when 90% scrolled
            
            if (isNearBottom) {
              // Set flags immediately to prevent duplicate calls
              fetchingChatsRef.current = true;
              setLoadingMoreChats(true);
              
              const nextPage = chatPage + 1;
              
              fetchChatRooms(showArchived, nextPage, chatPageLimit, true).then((result) => {
                if (result && result.hasMore) {
                  setHasMoreChats(result.hasMore);
                  setChatPage(nextPage);
                } else {
                  setHasMoreChats(false);
                }
              }).catch((error) => {
                console.error("Error loading more chats:", error);
                setHasMoreChats(false);
              }).finally(() => {
                // Always reset flags in finally block
                fetchingChatsRef.current = false;
                setLoadingMoreChats(false);
              });
            }
          }, 300);
        }}
        style={{ 
          marginTop: "12px", 
          margin: "15px",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          maxHeight: "calc(100vh - 200px)",
        }}
        className="chat-sidebar-scroll"
      >
        {isGlobalSearch ? (
          globalSearchLoading &&
          globalSearchResults.rooms.length === 0 &&
          globalSearchResults.messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
            </div>
          ) : globalSearchResults.rooms.length === 0 && globalSearchResults.messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6B7280", fontSize: "14px" }}>
              No results found
            </div>
          ) : (() => {
            const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
            const searchQ = (globalSearchQuery || "").toLowerCase().trim();

            const renderRoomTile = (result, key) => {
              const roomId = result._resolvedRoomId || result._id || result.roomId || result.id;
              if (!roomId) return null;
              const existing = allChats?.find(c => c.id === roomId || c.roomId === roomId || c._id === roomId);
              const isGroupChat = existing?.isGroup || result.type === "Group" || result.type === "group" || false;
              if (!ENABLE_GROUP_CHAT && isGroupChat) return null;
              const members = result.members || existing?.members || [];
              const otherMember = members.find(m => String(m._id || m.id) !== String(currentUserId));

              let name, avatarUrl, role = "", memberCount;
              if (existing?.name) {
                name = existing.name; avatarUrl = existing.avatarUrl; role = existing.role || ""; memberCount = existing.memberCount;
              } else if (isGroupChat) {
                name = result.title || result.name || "Group Chat";
                avatarUrl = result.logo ? getProfileImageUrl(result.logo) : null;
                memberCount = countNamedMembers(members);
              } else {
                const candidate = otherMember?.candidate;
                const rawName = candidate?.name || `${otherMember?.firstName || ""} ${otherMember?.lastName || ""}`.trim() || otherMember?.name || "";
                if (!rawName) return null;
                name = rawName.trim() || "Unknown";
                avatarUrl = (candidate?.profileImage || otherMember?.profileImage) ? getProfileImageUrl(candidate?.profileImage || otherMember?.profileImage) : null;
                role = existing?.role || "";
              }
              if (!role) {
                role = getGlobalSearchRoomRole(result, existing);
              }
              if (!name) return null;

              const isArchived = isRoomArchived(result, existing);

              if (searchQ) {
                const nameMatch = (name || "").toLowerCase().includes(searchQ);
                const roleMatch = (role || "").toLowerCase().includes(searchQ);
                const statusMatch = (existing?.statusLabel || "").toLowerCase().includes(searchQ);
                const locationMatch = (existing?.location || "").toLowerCase().includes(searchQ);
                const campaignMatch = (result.campaignName || "").toLowerCase().includes(searchQ);
                const advertMatch = (result.advertName || "").toLowerCase().includes(searchQ);
                const emailMatch = members.some((member) =>
                  (member.email || "").toLowerCase().includes(searchQ)
                );
                if (
                  !nameMatch &&
                  !roleMatch &&
                  !statusMatch &&
                  !locationMatch &&
                  !campaignMatch &&
                  !advertMatch &&
                  !emailMatch
                ) {
                  return null;
                }
              }

              const chatToOpen = existing
                ? { ...existing, isArchive: isArchived }
                : {
                    id: roomId,
                    roomId,
                    name,
                    role,
                    avatarUrl,
                    isGroup: isGroupChat,
                    memberCount: isGroupChat ? memberCount : undefined,
                    members,
                    time: "",
                    unreadCount: 0,
                    statusLabel: "",
                    isArchive: isArchived,
                  };
              const isSelected = selectedChat?.id === roomId || selectedChat?.roomId === roomId;
              const nameParts = (name || "").trim().split(" ");
              const initials = nameParts.length >= 2 ? (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase() : (name || "?").charAt(0).toUpperCase();

              return (
                <Card
                  key={key || roomId}
                  onClick={async () => {
                    setSelectedIndex(roomId); handleJoinRoom(roomId);
                    if (onChatSelect) onChatSelect(chatToOpen);
                    window.dispatchEvent(new CustomEvent("chatSelected", { detail: { roomId, chatId: roomId } }));
                    try { await API_MANAGER.getChatUnreadCount(); window.dispatchEvent(new CustomEvent("chatUnreadCountUpdated")); } catch {}
                  }}
                  style={{
                    borderRadius: isSelected ? "15px" : "12px",
                    border: isSelected ? "1px solid #E0B74E" : isGroupChat ? "1px solid #D1C4E9" : "1px solid #E5E7EB",
                    cursor: "pointer",
                    background: isSelected ? "#FAE7B6" : isArchived ? "#FFFBEB" : isGroupChat ? "#F3E8FF" : "#FFF",
                    transition: "all 0.3s ease",
                    marginBottom: "12px",
                  }}
                  bodyStyle={{ padding: "12px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Avatar size={48} src={isGroupChat ? (avatarUrl || null) : avatarUrl} icon={isGroupChat && !avatarUrl ? <TeamOutlined /> : undefined}
                      style={{ backgroundColor: isGroupChat ? "#7C3AED" : "#E5E7EB", color: isGroupChat ? "#fff" : undefined, fontSize: isGroupChat ? "22px" : undefined, flexShrink: 0 }}>
                      {!isGroupChat && !avatarUrl && initials}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: "15px", color: "#071135", overflow: isSmallScreen ? "visible" : "hidden", textOverflow: isSmallScreen ? "clip" : "ellipsis", whiteSpace: isSmallScreen ? "normal" : "nowrap", overflowWrap: isSmallScreen ? "anywhere" : "normal", wordBreak: isSmallScreen ? "break-word" : "normal", display: "block", flex: "1 1 0", minWidth: 0 }}>{name}</span>
                        {isArchived && <ArchivedBadge />}
                      </div>
                      {role && <span style={{ fontSize: "12px", color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{role}</span>}
                    </div>
                  </div>
                </Card>
              );
            };

            const renderMessageTile = (msg) => {
              const roomId = msg.roomId;
              if (!roomId) return null;
              const existing = allChats?.find(c => c.id === roomId || c.roomId === roomId || c._id === roomId);
              const searchRoom = globalSearchResults.rooms?.find(
                (room) =>
                  String(room._resolvedRoomId || room._id || room.roomId) === String(roomId)
              );
              const roomFromMsg = msg.room || msg.chatRoom || msg.roomDetails || searchRoom;
              const isGroupFromMsg = roomFromMsg?.type === "Group" || roomFromMsg?.type === "group";
              const name =
                existing?.name ||
                roomFromMsg?.title ||
                roomFromMsg?.name ||
                msg.roomTitle ||
                msg.chatName ||
                getSearchRoomDisplayName(searchRoom) ||
                "Private Chat";

              if (searchQ) {
                const nameMatch = (name || "").toLowerCase().includes(searchQ);
                const msgMatch = (msg.message || "").toLowerCase().includes(searchQ);
                const roleMatch =
                  ((existing?.role || "").toLowerCase()).includes(searchQ) ||
                  getGlobalSearchRoomRole(searchRoom, existing).toLowerCase().includes(searchQ);
                if (!nameMatch && !msgMatch && !roleMatch) return null;
              }
              const avatarUrl =
                existing?.avatarUrl ||
                (roomFromMsg?.logo ? getProfileImageUrl(roomFromMsg.logo) : null) ||
                null;
              const isGroupChat = existing?.isGroup || isGroupFromMsg || false;
              if (!ENABLE_GROUP_CHAT && isGroupChat) return null;
              const roleLine =
                (existing?.role && String(existing.role).trim()) ||
                getGlobalSearchRoomRole(searchRoom, existing) ||
                campaignAdvertLabelFromAdvertId(msg.advertId) ||
                campaignAdvertLabelFromAdvertId(roomFromMsg?.advertId);
              const nameParts = (name || "").trim().split(" ");
              const initials = nameParts.length >= 2 ? (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase() : (name || "?").charAt(0).toUpperCase();
              const isSelected = selectedChat?.id === roomId || selectedChat?.roomId === roomId;
              const isArchived = isRoomArchived(searchRoom || roomFromMsg || {}, existing);
              const chatToOpen = existing
                ? { ...existing, isArchive: isArchived, _searchMessageId: msg._id }
                : {
                    id: roomId,
                    roomId,
                    name,
                    role: roleLine,
                    avatarUrl,
                    isGroup: isGroupChat,
                    members: searchRoom?.members || roomFromMsg?.members || [],
                    time: "",
                    unreadCount: 0,
                    statusLabel: "",
                    isArchive: isArchived,
                    _searchMessageId: msg._id,
                  };

              return (
                <Card
                  key={msg._id}
                  onClick={async () => {
                    setSelectedIndex(roomId); handleJoinRoom(roomId);
                    if (onChatSelect) onChatSelect(chatToOpen);
                    window.dispatchEvent(new CustomEvent("chatSelected", { detail: { roomId, chatId: roomId } }));
                    try { await API_MANAGER.getChatUnreadCount(); window.dispatchEvent(new CustomEvent("chatUnreadCountUpdated")); } catch {}
                  }}
                  style={{
                    borderRadius: isSelected ? "12px" : "10px",
                    border: isSelected ? "1px solid #E0B74E" : isGroupChat ? "1px solid #D1C4E9" : "1px solid #E5E7EB",
                    width: "100%",
                    cursor: "pointer",
                    background: isSelected ? "#FAE7B6" : isArchived ? "#FFFBEB" : isGroupChat ? "#F3E8FF" : "#FFF",
                    transition: "all 0.3s ease",
                    marginBottom: "8px",
                  }}
                  bodyStyle={{ padding: "8px 10px" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Avatar
                      size={36}
                      src={isGroupChat ? (avatarUrl || null) : avatarUrl}
                      icon={isGroupChat && !avatarUrl ? <TeamOutlined /> : undefined}
                      style={{
                        backgroundColor: isGroupChat ? "#7C3AED" : "#E5E7EB",
                        color: isGroupChat ? "#fff" : undefined,
                        fontSize: isGroupChat ? "18px" : undefined,
                        flexShrink: 0,
                      }}
                    >
                      {!isGroupChat && !avatarUrl && initials}
                    </Avatar>

                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: "14px", color: "#071135", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 0", minWidth: 0 }}>{name}</span>
                        {isArchived && <ArchivedBadge />}
                      </div>
                      {roleLine && (
                        <span style={{ fontSize: "12px", color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{roleLine}</span>
                      )}
                      <span style={{ fontSize: "12px", color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: "italic" }}>
                        {isStatusStyleMessage(msg)
                          ? getStatusMessageLabel(msg, {
                              isOwn: false,
                              members: chatToOpen.members || [],
                            })
                          : getMessagePreviewText(msg, {
                              members: chatToOpen.members || [],
                            })}
                      </span>
                    </div>
                  </div>
                </Card>
              );
            };

            const filteredRoomTiles = globalSearchResults.rooms
              .map((room) => renderRoomTile(room, room._resolvedRoomId || room._id))
              .filter(Boolean);
            const filteredMsgTiles = globalSearchResults.messages.map(m => renderMessageTile(m)).filter(Boolean);

            if (filteredRoomTiles.length === 0 && filteredMsgTiles.length === 0) {
              return (
                <div style={{ textAlign: "center", padding: "40px", color: "#6B7280", fontSize: "14px" }}>
                  No results found
                </div>
              );
            }

            return (
              <>
                {filteredRoomTiles.length > 0 && (
                  <>
                    <div style={{ padding: "4px 4px 8px", fontSize: "11px", fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                      Chats
                    </div>
                    {filteredRoomTiles}
                  </>
                )}
                {filteredMsgTiles.length > 0 && (
                  <>
                    <div style={{ padding: "4px 4px 8px", fontSize: "11px", fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                      Messages
                    </div>
                    {filteredMsgTiles}
                  </>
                )}
              </>
            );
          })()
        ) : loadingChats ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (() => {
          const baseChats = ENABLE_GROUP_CHAT
            ? chats
            : (chats || []).filter((chat) => !(chat?.isGroup === true || chat?.type === "Group" || chat?.type === "group"));
          let filteredChats = showArchived 
            ? baseChats 
            : baseChats.filter((chat) => {
                if (activeKey === "unread") return chat.unreadCount > 0;
                if (activeKey === "read") return chat.unreadCount === 0;
                return true;
              });

          // Sort chats purely by most recent activity (WhatsApp-style) so the
          // chat with the latest sent/received message always jumps to the top,
          // regardless of read/unread state.
          filteredChats = [...filteredChats].sort((a, b) => {
            const timestampA = a.lastMessageTime || a.updatedAt;
            const timestampB = b.lastMessageTime || b.updatedAt;

            if (timestampA && timestampB) {
              const dateA = new Date(timestampA);
              const dateB = new Date(timestampB);
              // Most recent first (descending order)
              return dateB.getTime() - dateA.getTime();
            }

            // If one has timestamp and other doesn't, prioritize the one with timestamp
            if (timestampA && !timestampB) return -1;
            if (!timestampA && timestampB) return 1;

            return 0;
          });

          if (filteredChats.length === 0) {
            const isSearching = !!(
              globalSearchQuery && globalSearchQuery.trim().length > 0
            );
            // While a search is active and chats are still being loaded/matched,
            // show a spinner instead of flashing "No chats available".
            if (
              isSearching &&
              (globalSearchLoading || loadingMoreChats || hasMoreChats)
            ) {
              return (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="large" />
                </div>
              );
            }
            return (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#6B7280",
                  fontSize: "14px",
                }}
              >
                No {showArchived ? "archived" : activeKey === "unread" ? "unread" : activeKey === "read" ? "read" : ""} chats available
              </div>
            );
          }

          return filteredChats.map((chat, index) => {
            const isSelected = selectedChat?.id === chat.id || selectedIndex === chat.id;

            return (
              <Card
                key={chat.id || index}
                data-chat-id={chat.id}
                onClick={async (e) => {
                  e.stopPropagation();
                  // Always select the chat, don't unselect on double click
                  setSelectedIndex(chat.id);
                  
                  const roomId = chat.roomId || chat.id;
                  console.log("🖱️ Chat card clicked:", { 
                    chat, 
                    chatId: chat.id,
                    isSelected, 
                    roomId
                  });
                  
                  // Join room via WebSocket when chat is selected
                  // Note: ChatScreen will handle fetching messages via useEffect
                  if (chat.id) {
                    handleJoinRoom(chat.id);
                  }
                  
                  if (onChatSelect) {
                    console.log("📤 Calling onChatSelect with:", chat);
                    onChatSelect(chat);
                  }
                  
                  // Dispatch event to notify Layout about selected chat
                  window.dispatchEvent(new CustomEvent('chatSelected', {
                    detail: { roomId: roomId, chatId: chat.id }
                  }));
                  
                  // Call API to update unread count and trigger sidebar menu update
                  try {
                    await API_MANAGER.getChatUnreadCount();
                    window.dispatchEvent(new CustomEvent('chatUnreadCountUpdated'));
                  } catch (error) {
                    console.error("Failed to fetch unread count:", error);
                  }
                  
                  // Reset unread count when chat is selected
                  if (chat.unreadCount > 0) {
                    setChats((prevChats) => {
                      return prevChats.map((c) => {
                        if (c.id === chat.id || c.roomId === chat.roomId) {
                          return { ...c, unreadCount: 0 };
                        }
                        return c;
                      });
                    });
                  }
                }}
                style={{
                  borderRadius: isSelected ? "15px" : "12px",
                  border: isSelected ? "1px solid #E0B74E" : chat.isGroup ? "1px solid #D1C4E9" : "1px solid #E5E7EB",
                  padding: "12px",
                  width: "100%",
                  cursor: "pointer",
                  background: isSelected ? "#FAE7B6" : chat.isGroup ? "#F3E8FF" : "#FFF",
                  transition: "all 0.3s ease",
                  marginBottom: "12px",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  {/* Profile Image */}
                  <Avatar 
                    size={48} 
                    src={!chat.isGroup ? chat.avatarUrl : (chat.avatarUrl || null)}
                    icon={chat.isGroup && !chat.avatarUrl ? <TeamOutlined /> : undefined}
                    style={{
                      backgroundColor: chat.isGroup ? "#7C3AED" : "#E5E7EB",
                      color: chat.isGroup ? "#fff" : undefined,
                      fontSize: chat.isGroup ? "22px" : undefined,
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {!chat.isGroup && (() => {
                      const nameParts = chat.name.trim().split(" ");
                      if (nameParts.length >= 2) {
                        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
                      }
                      return chat.name.charAt(0).toUpperCase();
                    })()}
                  </Avatar>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
                    {/* Name and Time Row — keep actions pinned to the right */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        flexWrap: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flex: "1 1 0",
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      >
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!chat.isGroup && chat.otherMemberId) {
                              const storedRoomId = sessionStorage.getItem("leaveroom");
                              if (storedRoomId && socket && socket.connected) {
                                handleLeaveRoom(storedRoomId, socket);
                              }
                              navigate(`/applicant-profile/${chat.otherMemberId}`, {
                                state: {
                                  fromChat: true,
                                  chatId: chat.id || chat.roomId,
                                  selectedChat: chat,
                                  id: chat.advertId,
                                  advertId: chat.advertId,
                                },
                              });
                            }
                          }}
                          style={{
                            fontFamily: "Arial",
                            fontSize: "17px",
                            fontStyle: "normal",
                            fontWeight: 700,
                            lineHeight: "22px",
                            color: "#071135",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            flex: "1 1 0",
                            minWidth: 0,
                            cursor: (!chat.isGroup && chat.otherMemberId) ? "pointer" : "default",
                            transition: "color 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            if (!chat.isGroup && chat.otherMemberId) {
                              e.target.style.color = "#0A1444";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!chat.isGroup && chat.otherMemberId) {
                              e.target.style.color = "#071135";
                            }
                          }}
                          title={chat.name}
                        >
                          {chat.name}
                        </span>
                        {chat.isArchive && globalSearchQuery?.trim() && <ArchivedBadge />}
                        {chat.isGroup && (() => {
                          const visibleCount = Array.isArray(chat.members) && chat.members.length > 0
                            ? countNamedMembers(chat.members)
                            : (typeof chat.memberCount === "number" ? chat.memberCount : 0);
                          if (visibleCount <= 0) return null;
                          return (
                            <span
                              style={{
                                flexShrink: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: "20px",
                                backgroundColor: "#DCFCE7",
                                color: "#166534",
                                fontSize: "11px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                lineHeight: "16px",
                              }}
                            >
                              +{visibleCount} Member{visibleCount !== 1 ? "s" : ""}
                            </span>
                          );
                        })()}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexShrink: 0,
                          marginLeft: "auto",
                        }}
                      >
                        {/* Time */}
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 500,
                            lineHeight: "16px",
                            color: chat.unreadCount > 0 ? "#2F80ED" : "#6B7280",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.time}
                        </span>

                        {/* Return to Advert Icon */}
                        {chat.advertId && (
                          <Tooltip title="Return to advert" placement="top">
                            <img
                              src={MyIcon}
                              alt="Return to advert"
                              width="14"
                              height="14"
                              style={{ cursor: "pointer", flexShrink: 0, display: "block" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Leave room before navigating (session storage cleared in handleLeaveRoom callback)
                                const storedRoomId = sessionStorage.getItem("leaveroom");
                                if (storedRoomId && socket && socket.connected) {
                                  console.log("🚪 Navigating to advert detail, leaving room:", storedRoomId);
                                  handleLeaveRoom(storedRoomId, socket);
                                }
                                navigate(`/advert-detail/${chat.advertId}`, {
                                  state: {
                                    fromChat: true,
                                    chatId: chat.id || chat.roomId,
                                    selectedChat: chat,
                                    search: chat.name || "",
                                  },
                                });
                              }}
                            />
                          </Tooltip>
                        )}

                        {/* Archive/Unarchive Button */}
                        {showArchived ? (
                          <Tooltip title="Unarchive chat" placement="top">
                            <Button
                              type="text"
                              shape="circle"
                              icon={<img src={UnarchiveIcon} height={20} width={20} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnarchive(chat);
                              }}
                              style={{
                                flexShrink: 0,
                                width: 28,
                                height: 28,
                                minWidth: 28,
                                padding: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archive chat" placement="top">
                            <Button
                              type="text"
                              shape="circle"
                              icon={<img src={ArchiveIcon} height={20} width={20} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchive(chat);
                              }}
                              style={{
                                flexShrink: 0,
                                width: 28,
                                height: 28,
                                minWidth: 28,
                                padding: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    {chat.statusLabel && (
                      <div
                        style={{
                          display: "inline-flex",
                          padding: "4px 12px",
                          borderRadius: "6px",
                          backgroundColor: "#E3E9FC",
                          color: "#091647",
                          fontSize: "12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          width: "fit-content",
                          lineHeight: "16px",
                        }}
                      >
                        {chat.statusLabel}
                      </div>
                    )}

                    {/* Role + Location */}
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#071135",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      {/* Role */}
                      {chat.role && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            minWidth: 0,
                          }}
                        >
                          <span
                            role="img"
                            aria-label="bag"
                            style={{
                              width: 16,
                              height: 16,
                              flexShrink: 0,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M5.33398 14V4.66667C5.33398 4.04669 5.33398 3.7367 5.40213 3.48236C5.58707 2.79218 6.12616 2.25308 6.81635 2.06815C7.07068 2 7.38067 2 8.00065 2C8.62063 2 8.93062 2 9.18496 2.06815C9.87514 2.25308 10.4142 2.79218 10.5992 3.48236C10.6673 3.7367 10.6673 4.04669 10.6673 4.66667V14M3.46732 14H12.534C13.2807 14 13.6541 14 13.9393 13.8547C14.1902 13.7268 14.3942 13.5229 14.522 13.272C14.6673 12.9868 14.6673 12.6134 14.6673 11.8667V6.8C14.6673 6.05326 14.6673 5.6799 14.522 5.39468C14.3942 5.1438 14.1902 4.93982 13.9393 4.81199C13.6541 4.66667 13.2807 4.66667 12.534 4.66667H3.46732C2.72058 4.66667 2.34721 4.66667 2.062 4.81199C1.81111 4.93982 1.60714 5.1438 1.47931 5.39468C1.33398 5.6799 1.33398 6.05326 1.33398 6.8V11.8667C1.33398 12.6134 1.33398 12.9868 1.47931 13.272C1.60714 13.5229 1.81111 13.7268 2.062 13.8547C2.34721 14 2.72058 14 3.46732 14Z"
                                stroke="#071135"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: "18px",
                            }}
                            title={chat.role}
                          >
                            {chat.role}
                          </span>
                        </div>
                      )}
                      
                      {/* Location */}
                      {chat.location && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            minWidth: 0,
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              flexShrink: 0,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M7.99935 8.33203C9.10392 8.33203 9.99935 7.4366 9.99935 6.33203C9.99935 5.22746 9.10392 4.33203 7.99935 4.33203C6.89478 4.33203 5.99935 5.22746 5.99935 6.33203C5.99935 7.4366 6.89478 8.33203 7.99935 8.33203Z"
                                stroke="#071135"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M7.99935 14.6654C9.33268 11.9987 13.3327 10.2776 13.3327 6.66536C13.3327 3.71985 10.9449 1.33203 7.99935 1.33203C5.05383 1.33203 2.66602 3.71985 2.66602 6.66536C2.66602 10.2776 6.66602 11.9987 7.99935 14.6654Z"
                                stroke="#071135"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: "18px",
                            }}
                            title={chat.location}
                          >
                            {chat.location}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Unread Badge */}
                    {chat.unreadCount > 0 && !isSelected && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "4px",
                        }}
                      >
                        <Badge
                          count={chat.unreadCount || 0}
                          style={{
                            backgroundColor: "#2F80ED",
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: 600,
                            boxShadow: "none",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          });
        })()}
        
        {/* Loading indicator for pagination (only once results are shown, so it
            doesn't duplicate the initial/empty-state loader during search) */}
        {!isGlobalSearch && loadingMoreChats && chats?.length > 0 && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="small" />
          </div>
        )}
        
        {/* End of list indicator */}
        {/* {!hasMoreChats && chats.length > 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: "#6B7280",
              fontSize: "13px",
            }}
          >
            No more chats to load
          </div>
        )} */}
      </div>

      {/* Floating Chat Icon - Show when not on archive screen */}
      {!showArchived && (
        <Tooltip title="Start chats" placement="top">
          <Button
            type="primary"
            shape="circle"
            size="large"
            onClick={handleStartChatClick}
            style={{
              position: "absolute",
              bottom: "24px",
              right: "24px",
              width: "56px",
              height: "56px",
              backgroundColor: "rgb(255, 205, 60)",
              borderColor: "rgb(255, 205, 60)",
              color: "black",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              transition: "all 0.3s ease",
            }}
            icon={<MessageOutlined style={{ fontSize: "24px" }} />}
          />
        </Tooltip>
      )}

      {/* Start Chat Modal */}
      <Modal
        title="Start Chats"
        open={startChatModalVisible}
        onCancel={handleModalClose}
        centered
        getContainer={document.body}
        footer={null}
        width={600}
        style={{ top: 20 }}
        destroyOnClose={true}
        maskClosable={true}
        transitionName=""
        maskTransitionName=""
        bodyStyle={{
          padding: "20px",
          maxHeight: "70vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <>
          
            {ENABLE_GROUP_CHAT && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "16px",
                  background: "#F5F6FA",
                  borderRadius: "8px",
                  padding: "4px",
                }}
              >
                {["group", "private"].map((mode) => (
                  <div
                    key={mode}
                    onClick={() => {
                      setChatMode(mode);
                      setSelectedGroupMembers([]);
                    }}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      fontWeight: 600,
                      fontSize: "14px",
                      cursor: "pointer",
                      background: chatMode === mode ? "#0A1444" : "transparent",
                      color: chatMode === mode ? "#fff" : "#091647",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {mode === "private" ? "Private" : "Group"}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <Input
                placeholder="Search by candidate, email, campaign or advert."
                prefix={<SearchOutlined />}
                value={searchText}
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  borderRadius: "8px",
                  height: "40px",
                }}
              />
            </div>



            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={{
                overflowY: "auto",
                maxHeight: "calc(70vh - 200px)",
                paddingRight: "8px",
                flex: 1,
              }}
            >
              {(() => {
                const chatList = Array.isArray(allChats) ? allChats : [];
                
                const candidateIdsFromApp = (app) => {
                  const ids = new Set();
                  const add = (v) => {
                    if (v === null || v === undefined) return;
                    if (typeof v === "object") {
                      if (v._id) ids.add(String(v._id));
                      if (v.id) ids.add(String(v.id));
                      return;
                    }
                    const s = String(v);
                    if (s) ids.add(s);
                  };
                  add(app?.profileId?.userId);
                  add(app?.profileId?.userId?._id);
                  add(app?.profileId?._id);
                  add(app?.profileId);
                  add(app?.userId);
                  add(app?.userId?._id);
                  add(app?.candidate);
                  add(app?.candidate?._id);
                  add(app?.candidate?.userId);
                  add(app?.candidate?.userId?._id);
                  add(app?.profileId?.candidate);
                  add(app?.profileId?.candidate?._id);
                  return ids;
                };

                const memberIdsFromChatMember = (m) => {
                  const ids = new Set();
                  const add = (v) => {
                    if (v === null || v === undefined) return;
                    if (typeof v === "object") {
                      if (v._id) ids.add(String(v._id));
                      if (v.id) ids.add(String(v.id));
                      return;
                    }
                    const s = String(v);
                    if (s) ids.add(s);
                  };
                  if (m === null || m === undefined) return ids;
                  if (typeof m === "string") {
                    ids.add(m);
                    return ids;
                  }
                  add(m._id);
                  add(m.id);
                  add(m.userId);
                  add(m.userId?._id);
                  add(m.user);
                  add(m.user?._id);
                  add(m.profileId);
                  add(m.profileId?._id);
                  add(m.profileId?.userId);
                  add(m.profileId?.userId?._id);
                  add(m.candidate);
                  add(m.candidate?._id);
                  add(m.candidate?.userId);
                  add(m.candidate?.userId?._id);
                  return ids;
                };

                const idSetsIntersect = (setA, setB) => {
                  for (const v of setA) {
                    if (setB.has(v)) return true;
                  }
                  return false;
                };

                const visibleApplications = applications.filter((app) => {
                  const appAdvertId = String(app?.advertId?._id || app?.advertId || "");
                  if (!appAdvertId) return true;

                  const appCandidateIds = candidateIdsFromApp(app);
                  if (appCandidateIds.size === 0) return true;

                  const candidateUserId = String(
                    app?.profileId?.userId?._id || app?.profileId?.userId || ""
                  );

                  const chatMatchesCandidate = (chat) => {
                    const sameAdvert = String(chat.advertId || "") === appAdvertId;
                    if (!sameAdvert) return false;

                    const members = Array.isArray(chat.members) ? chat.members : [];
                    if (members.some((m) => idSetsIntersect(memberIdsFromChatMember(m), appCandidateIds))) {
                      return true;
                    }
                    if (candidateUserId && String(chat.otherMemberId || "") === candidateUserId) {
                      return true;
                    }
                    return false;
                  };

                  if (chatMode === "private") {
                    const hasPrivateChat = chatList.some((chat) => {
                      const isPrivate = chat.isGroup !== true && chat.type !== "Group" && chat.type !== "group";
                      if (!isPrivate) return false;
                      return chatMatchesCandidate(chat);
                    });
                    return !hasPrivateChat;
                  }

                  const hasGroupChat = chatList.some((chat) => {
                    const isGroup = chat.isGroup === true || chat.type === "Group" || chat.type === "group";
                    if (!isGroup) return false;
                    return chatMatchesCandidate(chat);
                  });
                  return !hasGroupChat;
                });

                return visibleApplications.length === 0 && !loading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6B7280",
                  }}
                >
                 No chats found! Try another search criteria.
                </div>
              ) : (
                visibleApplications.map((application, index) => {
                  const profileImage = getProfileImageUrl(
                    application?.profileId?.profileImage
                  );
                  const name = application?.profileId?.name || application?.candidateName || "N/A";
                  const displayText = getDisplayText(application);
                  const locationDetails = getLocationDetails(application);
                  return (
                    <Card
                      key={application._id || index}
                      onClick={() => {
                        if (chatMode === "group") {
                          handleGroupCandidateSelect(application);
                        } else {
                          handleCandidateClick(application);
                        }
                      }}
                      style={{
                        marginBottom: "12px",
                        borderRadius: "12px",
                        border: "1.5px solid #D0D5DD",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        background: "#fff",
                      }}
                      bodyStyle={{ padding: "12px" }}
                      hoverable
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Avatar
                          size={48}
                          src={profileImage}
                          style={{
                            backgroundColor: "#E5E7EB",
                            flexShrink: 0,
                          }}
                        >
                          {(() => {
                            const nameParts = name.trim().split(" ");
                            if (nameParts.length >= 2) {
                              return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
                            }
                            return name.charAt(0).toUpperCase();
                          })()}
                        </Avatar>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontFamily: "Arial",
                              fontSize: "20px",
                              fontWeight: 700,
                              color: "#071135",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {name}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#071135",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginTop: "2px",
                            }}
                          >
                            {displayText}
                          </div>
                          {locationDetails && (
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#071135",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                marginTop: "4px",
                                overflow: "hidden",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 16 16"
                                fill="none"
                                style={{ flexShrink: 0 }}
                              >
                                <path
                                  d="M7.99935 8.33203C9.10392 8.33203 9.99935 7.4366 9.99935 6.33203C9.99935 5.22746 9.10392 4.33203 7.99935 4.33203C6.89478 4.33203 5.99935 5.22746 5.99935 6.33203C5.99935 7.4366 6.89478 8.33203 7.99935 8.33203Z"
                                  stroke="#071135"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7.99935 14.6654C9.33268 11.9987 13.3327 10.2776 13.3327 6.66536C13.3327 3.71985 10.9449 1.33203 7.99935 1.33203C5.05383 1.33203 2.66602 3.71985 2.66602 6.66536C2.66602 10.2776 6.66602 11.9987 7.99935 14.6654Z"
                                  stroke="#071135"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{locationDetails}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })
              );
              })()}

              {loading && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Spin size="large" />
                </div>
              )}
            </div>

          </>
      </Modal>
    </div>
  );
};

export default ChatSidebar;
